import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Spinner, Tooltip, Select, SelectItem } from '@nextui-org/react';
import {
  useGetPronunciationDictionaryByNameQuery,
  useDeletePronunciationDictionaryMutation,
  useDeletePronunciationDictionaryItemMutation,
  useGetAiWorkersByCategoryQuery,
  useGetAgentQuery,
  useGetAllowedModelsQuery,
  categoryType,
  normalizeAiWorkersList,
  findAiWorkersUsingDictionary,
  getTtsProviderOptions,
  getTtsVoiceOptionsForProvider,
} from '@circuitry-ai/doc-data';
import {
  CtyButton,
  CtyShowToast,
  checkType,
  ErrorBoundary,
  SimpleDeleteConfirmationModal,
} from '@circuitry-ai/doc-ui';
import { useVoicePreview } from './useVoicePreview';
import DictionaryEntryModal, { type DictionaryEntryModalContext } from './DictionaryEntryModal';
import { DictionaryImportExport } from './DictionaryImportExport';

interface DictionaryDetailPanelProps {
  dictionaryName: string;
  onDeleted: () => void;
}

const DictionaryEntryModalWithErrorBoundary = ErrorBoundary(DictionaryEntryModal);
const SimpleDeleteConfirmationModalWithErrorBoundary = ErrorBoundary(
  SimpleDeleteConfirmationModal as unknown as React.ComponentType<Record<string, unknown>>
);

const DictionaryDetailPanel: React.FC<DictionaryDetailPanelProps> = ({
  dictionaryName,
  onDeleted,
}) => {
  const { t } = useTranslation();
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [entryModalMode, setEntryModalMode] = useState<'create' | 'edit'>('create');
  const [entryContext, setEntryContext] = useState<DictionaryEntryModalContext>({
    dictionaryName,
  });
  const [deleteDictionaryModalOpen, setDeleteDictionaryModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<string | null>(null);
  const [previewProvider, setPreviewProvider] = useState('');
  const [previewVoiceId, setPreviewVoiceId] = useState('');
  const { playPreview, previewingKey, loadingKey } = useVoicePreview();

  const {
    data: dictionary,
    isLoading,
    isFetching,
    refetch,
    isError,
    error,
  } = useGetPronunciationDictionaryByNameQuery(dictionaryName, {
    skip: !dictionaryName,
  });

  const [deleteDictionary, { isLoading: isDeletingDictionary }] =
    useDeletePronunciationDictionaryMutation();
  const [deleteItem, { isLoading: isDeletingItem }] =
    useDeletePronunciationDictionaryItemMutation();
  const { data: aiWorkersResponse } = useGetAiWorkersByCategoryQuery(
    { category: categoryType.AGENT },
    { skip: !dictionaryName }
  );
  const { data: voiceAgents = [] } = useGetAgentQuery(undefined, { skip: !dictionaryName });
  const { data: allowedModels } = useGetAllowedModelsQuery(undefined, { skip: !dictionaryName });

  const ttsEntries = allowedModels?.allowed_models?.tts ?? [];
  const previewProviderOptions = useMemo(
    () => getTtsProviderOptions(ttsEntries),
    [ttsEntries]
  );
  const previewVoiceOptions = useMemo(
    () => getTtsVoiceOptionsForProvider(ttsEntries, previewProvider),
    [previewProvider, ttsEntries]
  );

  useEffect(() => {
    if (previewProviderOptions.length === 0) return;
    const providerStillValid = previewProviderOptions.some((option) => option.value === previewProvider);
    if (!previewProvider || !providerStillValid) {
      setPreviewProvider(previewProviderOptions[0].value);
    }
  }, [previewProvider, previewProviderOptions]);

  useEffect(() => {
    if (previewVoiceOptions.length === 0) {
      setPreviewVoiceId('');
      return;
    }
    const voiceStillValid = previewVoiceOptions.some((option) => option.value === previewVoiceId);
    if (!previewVoiceId || !voiceStillValid) {
      setPreviewVoiceId(previewVoiceOptions[0].value);
    }
  }, [previewVoiceId, previewVoiceOptions]);

  const aiWorkers = useMemo(
    () => normalizeAiWorkersList(aiWorkersResponse),
    [aiWorkersResponse]
  );

  const dictionaryId = dictionary?.id ?? '';
  const items = dictionary?.items ?? [];

  const handleCopyId = useCallback(async () => {
    if (!dictionaryId) return;
    try {
      await navigator.clipboard.writeText(dictionaryId);
      CtyShowToast(checkType.success, t('Dictionary ID copied.'));
    } catch {
      CtyShowToast(checkType.error, t('Failed to copy dictionary ID.'));
    }
  }, [dictionaryId, t]);

  const handleAddEntry = useCallback(() => {
    setEntryModalMode('create');
    setEntryContext({ dictionaryName });
    setEntryModalOpen(true);
  }, [dictionaryName]);

  const handleEditEntry = useCallback(
    (text: string, pronunciation: string) => {
      setEntryModalMode('edit');
      setEntryContext({ dictionaryName, text, pronunciation });
      setEntryModalOpen(true);
    },
    [dictionaryName]
  );

  const handleDeleteDictionary = useCallback(async () => {
    const dictionaryId = String(dictionary?.id ?? '').trim();
    if (!dictionaryId) {
      CtyShowToast(checkType.error, t('Dictionary id is missing.'));
      setDeleteDictionaryModalOpen(false);
      return;
    }

    const attachedAgents = findAiWorkersUsingDictionary(
      dictionaryId,
      aiWorkers,
      voiceAgents as Array<Record<string, unknown>>
    );
    if (attachedAgents.length > 0) {
      const agentNames = attachedAgents.map((agent) => agent.name).filter(Boolean);
      const agentLabel = agentNames.length === 1 ? agentNames[0] : agentNames.join(', ');
      CtyShowToast(
        checkType.error,
        t('Dictionary "{{dictionary}}" is attached to {{agent}} and cannot be deleted.', {
          dictionary: dictionaryName,
          agent: agentLabel,
        })
      );
      setDeleteDictionaryModalOpen(false);
      return;
    }

    try {
      await deleteDictionary(dictionaryName).unwrap();
      CtyShowToast(checkType.success, t('Dictionary deleted successfully.'));
      setDeleteDictionaryModalOpen(false);
      onDeleted();
    } catch (err) {
      CtyShowToast(
        checkType.error,
        (err as { data?: { message?: string } })?.data?.message ||
          (err as Error)?.message ||
          t('Failed to delete dictionary.')
      );
    }
  }, [aiWorkers, deleteDictionary, dictionary?.id, dictionaryName, onDeleted, t, voiceAgents]);

  const handlePreviewEntry = useCallback(
    (itemKey: string, pronunciation: string) => {
      void playPreview({
        key: itemKey,
        voiceId: previewVoiceId,
        transcript: pronunciation,
        provider: previewProvider || undefined,
      });
    },
    [playPreview, previewProvider, previewVoiceId]
  );

  const handleDeleteEntry = useCallback(async () => {
    if (!entryToDelete) return;
    try {
      await deleteItem({ dictionaryName, text: entryToDelete }).unwrap();
      CtyShowToast(checkType.success, t('Entry deleted successfully.'));
      setEntryToDelete(null);
      await refetch();
    } catch (err) {
      CtyShowToast(
        checkType.error,
        (err as { data?: { message?: string } })?.data?.message ||
          (err as Error)?.message ||
          t('Failed to delete entry.')
      );
    }
  }, [deleteItem, dictionaryName, entryToDelete, refetch, t]);

  const savedLabel = useMemo(() => {
    if (isFetching) return t('Saving...');
    return t('Saved automatically');
  }, [isFetching, t]);

  if (isLoading) {
    return (
      <div className="flex flex-1 min-h-[320px] items-center justify-center gap-2 rounded-xl border border-divider bg-content1">
        <Spinner size="sm" />
        <span className="text-sm text-default-500">{t('Loading dictionary...')}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 min-h-[320px] items-center justify-center rounded-xl border border-danger/30 bg-danger/10 px-4 text-sm text-danger">
        {(error as { data?: { message?: string } })?.data?.message ||
          (error as Error)?.message ||
          t('Failed to load dictionary.')}
      </div>
    );
  }

  return (
    <section className="flex-1 min-w-0 rounded-xl border border-divider bg-content1 p-4 sm:p-5 flex flex-col gap-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-xl font-semibold text-foreground truncate">{dictionaryName}</h3>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <DictionaryImportExport
            dictionaryId={dictionaryId}
            dictionaryName={dictionaryName}
            onImported={refetch}
          />
          <Tooltip content={t('Delete dictionary')}>
            <button
              type="button"
              onClick={() => setDeleteDictionaryModalOpen(true)}
              className="p-2 rounded-lg text-danger hover:bg-danger/10"
              aria-label={t('Delete dictionary')}
            >
              <Icon icon="hugeicons:delete-02" className="w-5 h-5" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold tracking-wide text-default-500 uppercase mb-2">
          {t('Dictionary ID')}
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-divider bg-default-50 px-3 py-2">
          <code className="flex-1 text-sm text-foreground truncate">{dictionaryId || '—'}</code>
          <Tooltip content={t('Copy')}>
            <button
              type="button"
              onClick={() => void handleCopyId()}
              disabled={!dictionaryId}
              className="p-1.5 rounded-md text-default-500 hover:bg-default-100 disabled:opacity-40"
              aria-label={t('Copy dictionary ID')}
            >
              <Icon icon="solar:copy-linear" className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs font-semibold tracking-wide text-default-500 uppercase">
            {t('Entries')} · {items.length}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-success">
            <Icon icon="mdi:check-circle-outline" className="w-4 h-4" />
            {savedLabel}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            label={t('Provider')}
            placeholder={t('Select provider')}
            selectedKeys={previewProvider ? new Set([previewProvider]) : new Set()}
            onSelectionChange={(keys) => {
              const nextProvider = Array.from(keys)[0] as string | undefined;
              setPreviewProvider(nextProvider ?? '');
            }}
            variant="bordered"
            isDisabled={previewProviderOptions.length === 0}
          >
            {previewProviderOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
          <Select
            label={t('Voice')}
            placeholder={t('Select voice')}
            selectedKeys={previewVoiceId ? new Set([previewVoiceId]) : new Set()}
            onSelectionChange={(keys) => {
              const nextVoice = Array.from(keys)[0] as string | undefined;
              setPreviewVoiceId(nextVoice ?? '');
            }}
            variant="bordered"
            isDisabled={previewVoiceOptions.length === 0}
          >
            {previewVoiceOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </Select>
        </div>

        <div className="rounded-xl border border-divider overflow-hidden max-h-[calc(100vh-500px)] overflow-y-auto">
          <div className="sticky top-0 z-10 grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-2 bg-default-50 border-b border-divider text-xs font-semibold tracking-wide text-default-500 uppercase">
            <span>{t('Word')}</span>
            <span>{t('Pronunciation')}</span>
            <span className="w-8" aria-hidden="true" />
            <span className="w-8" />
          </div>

          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-default-500">
              {t('No entries yet. Add your first pronunciation entry.')}
            </div>
          ) : (
            <div className="divide-y divide-divider">
              {items.map((item, index) => {
                const itemKey = `${item.text}-${index}`;
                const isPreviewLoading = loadingKey === itemKey;
                const isPreviewPlaying = previewingKey === itemKey;
                return (
                  <div
                    key={`${item.text}-${item.pronunciation}-${index}`}
                    className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-3 items-center hover:bg-default-50 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => handleEditEntry(item.text, item.pronunciation)}
                      className="text-left font-semibold text-foreground hover:text-primary"
                    >
                      {item.text}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEditEntry(item.text, item.pronunciation)}
                      className="text-left text-default-600 hover:text-primary"
                    >
                      <span className="text-default-400 mr-2">→</span>
                      {item.pronunciation}
                    </button>
                    <Tooltip content={t('Preview pronunciation')}>
                      <button
                        type="button"
                        onClick={() => handlePreviewEntry(itemKey, item.pronunciation)}
                        disabled={!previewVoiceId || !item.pronunciation?.trim()}
                        className="p-1.5 rounded-md text-primary hover:bg-primary/10 disabled:opacity-40"
                        aria-label={t('Preview pronunciation')}
                      >
                        {isPreviewLoading ? (
                          <Spinner size="sm" color="primary" />
                        ) : (
                          <Icon
                            icon={isPreviewPlaying ? 'solar:pause-circle-bold' : 'solar:play-circle-bold'}
                            className="w-5 h-5"
                          />
                        )}
                      </button>
                    </Tooltip>
                    <Tooltip content={t('Delete entry')}>
                      <button
                        type="button"
                        onClick={() => setEntryToDelete(item.text)}
                        className="p-1.5 rounded-md text-danger hover:bg-danger/10"
                        aria-label={t('Delete entry')}
                      >
                        <Icon icon="hugeicons:delete-02" className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <CtyButton
          variant="bordered"
          className="w-full"
          onPress={handleAddEntry}
          startContent={<Icon icon="mdi:plus" className="w-4 h-4" />}
        >
          {t('Add entry')}
        </CtyButton>
      </div>

      {entryModalOpen && (
        <DictionaryEntryModalWithErrorBoundary
          isOpen={entryModalOpen}
          onClose={() => setEntryModalOpen(false)}
          onSuccess={() => refetch()}
          mode={entryModalMode}
          context={entryContext}
        />
      )}

      {deleteDictionaryModalOpen && (
        <SimpleDeleteConfirmationModalWithErrorBoundary
          isOpen={deleteDictionaryModalOpen}
          onOpenChange={setDeleteDictionaryModalOpen}
          entityName={dictionaryName}
          onConfirm={() => handleDeleteDictionary()}
          isConfirmLoading={isDeletingDictionary}
          cancelLabel={t('Cancel')}
          confirmLabel={t('Delete dictionary')}
        />
      )}

      {entryToDelete && (
        <SimpleDeleteConfirmationModalWithErrorBoundary
          isOpen={Boolean(entryToDelete)}
          onOpenChange={(open: boolean) => {
            if (!open) setEntryToDelete(null);
          }}
          entityName={entryToDelete}
          onConfirm={() => handleDeleteEntry()}
          isConfirmLoading={isDeletingItem}
          cancelLabel={t('Cancel')}
          confirmLabel={t('Delete entry')}
        />
      )}
    </section>
  );
};

export default DictionaryDetailPanel;
